import {useMutation} from '@tanstack/react-query';

import {TypeLocalNameEnum} from 'domain/usecases';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {getHealthProviderLocationSupportAccredited} from '../../services/getHealthProviderLocationSupportAccredited';
import {FeaturesEnum} from 'presentation/constants/featuresFlagEnum';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useQueryParams} from 'presentation/pages';
import {MIN_DISTANCE_IN_KM_HEALTH} from 'presentation/modules/booking/health/ui/components/HealthSearchBar/hooks/useHealthSearchBar/constants';

export function useGetHealthProviderAccreditedMutation() {
	const bookingData = useBookingStore(state => state.bookingData);
	const {isEnabled: showUnitDistanceIsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SHOW_UNIT_DISTANCE_HEALTH_GEOLOC_BOOKING,
	);

	const {
		SEARCH_PARAMS: {localityDistance},
	} = useQueryParams();

	const {
		specialtyCode,
		subSpecialtyCode,
		ufCode,
		isManualLocation,
		cityName,
		typeLocalName,
		coordinates,
		districtName,
	} = bookingData;

	const isUsingGeolocation = !isManualLocation;

	const distance = () => {
		if (isUsingGeolocation) {
			return localityDistance || MIN_DISTANCE_IN_KM_HEALTH;
		}

		return !localityDistance && districtName ? undefined : localityDistance;
	};

	const baseParams = {
		specialtyCode: String(specialtyCode),
		subSpecialtyCode: subSpecialtyCode ? String(subSpecialtyCode) : '',
	};

	const paramsWithNoDistance = {
		...baseParams,
		stateCode: ufCode?.toUpperCase(),
		cityName: cityName?.toUpperCase(),
		typeLocalName: isUsingGeolocation
			? TypeLocalNameEnum.CIDADE
			: typeLocalName || TypeLocalNameEnum.CIDADE,
	};

	const paramsShowDistance = {
		...baseParams,
		typeLocalName: typeLocalName || TypeLocalNameEnum.CIDADE,
		stateCode: ufCode?.toUpperCase(),
		cityName: cityName?.toUpperCase(),
		coordinates,
		distance: distance(),
	};

	const params = showUnitDistanceIsEnabled ? paramsShowDistance : paramsWithNoDistance;

	return useMutation({
		mutationFn: async ({signal}: {signal?: AbortSignal}) =>
			getHealthProviderLocationSupportAccredited({
				params: {signal, ...params},
			}),
	});
}
